import { Button } from "@nimit9/signet-ui/components/button"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  Briefcase,
  Calculator,
  CalendarClock,
  CalendarRange,
  ChevronRight,
  CreditCard,
  FileText,
  Flag,
  Landmark,
  type LucideIcon,
  RefreshCw,
  Settings,
  Shield,
  Target,
  TrendingUp,
} from "lucide-react"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { useHousehold } from "@/hooks/use-household"
import { signOut } from "@/lib/sign-out"

export const Route = createFileRoute("/more")({
  component: More,
})

const groups: Array<{
  label: string
  items: Array<{ label: string; to: string; description: string; icon: LucideIcon }>
}> = [
  {
    label: "Daily",
    items: [
      {
        label: "Budgets",
        to: "/budgets",
        description: "Monthly limits per category",
        icon: Target,
      },
      {
        label: "Upcoming",
        to: "/upcoming",
        description: "Bills and EMIs due soon",
        icon: CalendarClock,
      },
      {
        label: "Subscriptions",
        to: "/subscriptions",
        description: "Recurring charges",
        icon: RefreshCw,
      },
    ],
  },
  {
    label: "Wealth",
    items: [
      {
        label: "Net Worth",
        to: "/net-worth",
        description: "Assets minus liabilities",
        icon: TrendingUp,
      },
      {
        label: "Investments",
        to: "/investments",
        description: "Portfolio, SIPs and returns",
        icon: Briefcase,
      },
      { label: "Goals", to: "/goals", description: "Savings targets", icon: Flag },
      { label: "Debt", to: "/debt", description: "Loans and what you owe", icon: CreditCard },
      { label: "Insurance", to: "/insurance", description: "Policies and cover", icon: Shield },
    ],
  },
  {
    label: "Tools",
    items: [
      { label: "Tax", to: "/tax", description: "Estimate, capital gains, 80C", icon: Landmark },
      {
        label: "Calculators",
        to: "/calculators",
        description: "EMI, SIP, FIRE, tax and more",
        icon: Calculator,
      },
      { label: "Reports", to: "/reports", description: "Monthly summaries", icon: FileText },
      { label: "Heatmap", to: "/heatmap", description: "Spending by day", icon: CalendarRange },
    ],
  },
]

function More() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const { data: householdData } = useHousehold()
  const members = householdData?.members ?? []
  const displayName = members.map((m) => m.name || m.email.split("@")[0]).join(" + ")
  const initial = (members[0]?.name?.[0] || members[0]?.email?.[0] || "?").toUpperCase()

  return (
    <>
      <Header title="More" showPeriod={false} />
      <PageWrapper>
        {isDesktop ? (
          <MoreDesktop displayName={displayName} initial={initial} />
        ) : (
          <MorePhone displayName={displayName} initial={initial} />
        )}
      </PageWrapper>
    </>
  )
}

type HouseholdProps = { displayName: string; initial: string }

/** Household + sign out — mirrors the sidebar's bottom section. */
function HouseholdRow({ displayName, initial }: HouseholdProps) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border-subtle bg-bg-surface px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-green/20 text-body text-accent-green">
          {initial}
        </div>
        <span className="truncate text-body text-text-secondary">{displayName}</span>
      </div>
      <Button variant="ghost" size="sm" onClick={() => signOut()}>
        Sign out
      </Button>
    </div>
  )
}

/**
 * Desktop: the same groups as a width-capped grid of link cards (icon, label,
 * one-line description) — no full-width stretched rows. Settings closes the
 * page as its own "Account" group.
 */
function MoreDesktop(props: HouseholdProps) {
  const desktopGroups = [
    ...groups,
    {
      label: "Account",
      items: [
        {
          label: "Settings",
          to: "/settings",
          description: "Profile, household, accounts and privacy",
          icon: Settings,
        },
      ],
    },
  ]
  return (
    <div data-testid="more-desktop" className="mx-auto w-full max-w-5xl space-y-8">
      <HouseholdRow {...props} />
      {desktopGroups.map((group) => (
        <section key={group.label} aria-labelledby={`more-group-${group.label}`}>
          <h2
            id={`more-group-${group.label}`}
            className="mb-3 px-1 text-label font-semibold uppercase tracking-wider text-text-hint"
          >
            {group.label}
          </h2>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            {group.items.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex min-w-0 items-center gap-3 rounded-xl bg-bg-surface p-4 ring-1 ring-border-subtle transition-colors hover:bg-bg-elevated hover:ring-border-default"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-green/10 text-accent-green">
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-callout font-medium text-text-primary">
                      {item.label}
                    </span>
                    <span className="truncate text-caption text-text-muted">
                      {item.description}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-text-hint" aria-hidden />
                </Link>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}

/** Phone: the grouped menu list (unchanged). */
function MorePhone(props: HouseholdProps) {
  return (
    <div data-testid="more-phone" className="mx-auto max-w-lg space-y-6">
      <HouseholdRow {...props} />

      {groups.map((group) => (
        <div key={group.label}>
          <p className="mb-2 px-1 text-label font-semibold tracking-wider text-text-hint">
            {group.label}
          </p>
          <div className="overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
            {group.items.map((item, i) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={
                    "flex items-center gap-3 px-4 py-3 text-body text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary" +
                    (i > 0 ? " border-t border-border-subtle" : "")
                  }
                >
                  <Icon className="h-4 w-4 shrink-0 text-text-muted" />
                  <span className="flex-1">{item.label}</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-text-hint" />
                </Link>
              )
            })}
          </div>
        </div>
      ))}

      <div className="overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
        <Link
          to="/settings"
          className="flex items-center gap-3 px-4 py-3 text-body text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary"
        >
          <Settings className="h-4 w-4 shrink-0 text-text-muted" />
          <span className="flex-1">Settings</span>
          <ChevronRight className="h-4 w-4 shrink-0 text-text-hint" />
        </Link>
      </div>
    </div>
  )
}
